import { FormEvent, Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Bookmark,
  Check,
  ChevronDown,
  Clock3,
  Copy,
  Filter,
  Gauge,
  History,
  MessageSquare,
  Pause,
  Play,
  Search,
  UserRoundCheck,
  Volume2
} from "lucide-react";
import { apiUrl, responseError } from "../app/api";
import { DEMO_VERSION_ID } from "../app/demo";
import { FEATURE_FLAGS } from "../app/feature-flags";
import { minuteLineMap } from "../app/minute-transcript";
import { previewPageGroups } from "../app/preview-pagination";
import { diffTranscript } from "../app/transcript-diff";
import { displayTranscriptTitle } from "../app/transcript-title";
import { formatTranscriptWallClock } from "../app/transcript-time";
import type {
  EditRecord,
  MatterDraft,
  ReviewReason,
  TranscriptRow,
  TranscriptReviewEvent,
  TranscriptReviewEventKind,
  TranscriptSession,
  TranscriptView
} from "../app/ui-types";

type ReviewWorkspaceProps = {
  initialRows?: TranscriptRow[];
  matter: MatterDraft;
  session: TranscriptSession | null;
  onRowsChange: (rows: TranscriptRow[]) => void;
  onSessionChange: (session: TranscriptSession) => void;
  reviewerName: string;
  onOpenExport: () => void;
  onTitleChange?: (title: string) => void;
  compositeMatterId?: string;
  readOnly?: boolean;
  onFinalise?: () => Promise<void>;
  onReloadComposite?: () => Promise<TranscriptRow[]>;
};

const REASON_LABELS: Record<ReviewReason, string> = {
  "low-confidence": "Low confidence",
  "uncertain-speaker": "Speaker",
  "provider-disagreement": "Check against audio",
  overlap: "Overlap",
  inaudible: "Inaudible",
  name: "Name",
  number: "Number",
  date: "Date",
  address: "Address"
};

const timecode = (milliseconds: number) => {
  const seconds = Math.floor(milliseconds / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(
    2,
    "0"
  )}`;
};

const transcriptTime = (row: TranscriptRow, recordedAtEpochMs?: number) =>
  row.recordedAtMs || recordedAtEpochMs
    ? formatTranscriptWallClock(
        row.recordedAtMs ?? (recordedAtEpochMs ?? 0) + row.startMs
      )
    : timecode(row.startMs);

const confidenceClass = (score: number) =>
  score < 65 ? "confidence-word--low" : score < 85 ? "confidence-word--medium" : "";

const confidenceBand = (score: number) =>
  score < 65 ? "low" : score < 85 ? "medium" : "high";

export const cleanTranscriptClipboardRow = (row: TranscriptRow) => {
  if (row.kind === "exhibit") {
    return `EXHIBIT: ${row.text.replace(/^Exhibit\s*[—-]\s*/i, "")}`;
  }
  if (row.kind === "adjournment") {
    const detail = row.text.replace(/^Adjournment(?:\s*[—-]\s*)?/i, "").trim();
    return detail ? `ADJOURNMENT: ${detail}` : "ADJOURNMENT";
  }
  if (row.kind && row.kind !== "speech") {
    return `${row.kind.toUpperCase()}: ${row.text}`;
  }
  return `${row.speaker || "UNKNOWN SPEAKER"}: ${row.text}`;
};

export const cleanTranscriptClipboardPage = (
  page: number,
  rows: readonly TranscriptRow[]
) => `Page ${page}\n\n${rows.map(cleanTranscriptClipboardRow).join("\n\n")}`;

export const cleanTranscriptClipboard = (rows: readonly TranscriptRow[]) =>
  rows.map(cleanTranscriptClipboardRow).join("\n\n");

/**
 * Transcript rows show a short role marker beside each line. It has to come
 * from the matter's own participants — hardcoding demo names put "MS TAN"
 * against real speakers in every recording.
 */
const roleMarker = (
  participants: readonly { name: string; role: string }[],
  speakerName: string
) => {
  const participant = participants.find((entry) => entry.name === speakerName);
  if (!participant) return speakerName.toUpperCase();
  const role = participant.role.trim();
  return (role || participant.name).toUpperCase();
};

type ReviewOperationSummary = {
  kind: string;
  targetNodeId?: string;
  beforeText?: string;
  afterText?: string;
  [key: string]: unknown;
};

type ReviewTranscriptNode = {
  id: string;
  kind?: string;
  mark?: "unchanged" | "addition" | "deletion";
  text: string;
  sourceNodeId?: string | null;
  timeRange: { startMs: number; endMs: number };
  speaker: { speakerId: string | null; label: string } | null;
  confidence: {
    wordConfidence: number | null;
    speakerConfidence: number | null;
    alignmentConfidence: number | null;
    mismatch: boolean;
    overlap: boolean;
  };
};

type ReviewTranscriptPayload = {
  baseline: { nodes: ReviewTranscriptNode[] };
  projection: { nodes: ReviewTranscriptNode[] };
};

const materiallyEditedTranscriptNodes = (
  transcript: ReviewTranscriptPayload
) => {
  const visibleBySourceId = new Map(
    transcript.projection.nodes
      .filter((node) => node.mark !== "deletion" && node.sourceNodeId)
      .map((node) => [node.sourceNodeId as string, node])
  );
  return transcript.baseline.nodes.flatMap((baseline) => {
    const current = visibleBySourceId.get(baseline.id);
    return !current ||
      current.text !== baseline.text ||
      current.speaker?.speakerId !== baseline.speaker?.speakerId ||
      current.speaker?.label !== baseline.speaker?.label
      ? [baseline.id]
      : [];
  });
};

const mergedReviewEvents = (
  current: TranscriptReviewEvent[],
  incoming: TranscriptReviewEvent[]
) => {
  const incomingIds = new Set(incoming.map((event) => event.id));
  return [
    ...incoming,
    ...current.filter((event) => !incomingIds.has(event.id))
  ];
};

const confidencePercentage = (value: number | null) =>
  Math.max(0, Math.min(100, Math.round((value ?? 0) * 100)));

const rowsFromTranscript = (transcript: {
  baseline: { nodes: ReviewTranscriptNode[] };
  projection: { nodes: ReviewTranscriptNode[] };
}): TranscriptRow[] => {
  const baselineById = new Map(transcript.baseline.nodes.map((node) => [node.id, node]));
  return transcript.projection.nodes.map((node, index) => {
    const baseline =
      baselineById.get(node.sourceNodeId ?? node.id)?.text ??
      baselineById.get(node.id)?.text ??
      node.text;
    const wordConfidence = confidencePercentage(node.confidence.wordConfidence);
    const speakerConfidence = confidencePercentage(node.confidence.speakerConfidence);
    const alignmentConfidence = confidencePercentage(node.confidence.alignmentConfidence);
    const reasons: ReviewReason[] = [
      ...(node.confidence.wordConfidence === null ||
      node.confidence.wordConfidence < 0.65
        ? (["low-confidence"] as ReviewReason[])
        : []),
      ...((node.kind ?? "speech") === "speech" &&
      (node.confidence.speakerConfidence === null ||
        node.confidence.speakerConfidence < 0.65)
        ? (["uncertain-speaker"] as ReviewReason[])
        : []),
      ...(node.confidence.mismatch
        ? (["provider-disagreement"] as ReviewReason[])
        : []),
      ...(node.confidence.overlap ? (["overlap"] as ReviewReason[]) : [])
    ];
    return {
      id: node.sourceNodeId ?? node.id,
      kind: (node.kind ?? "speech") as TranscriptRow["kind"],
      line: index + 1,
      startMs: node.timeRange.startMs,
      endMs: node.timeRange.endMs,
      speakerId: node.speaker?.speakerId ?? "unknown",
      speaker: node.speaker?.label ?? "Unknown speaker",
      role: node.speaker?.label ?? "RECORDER",
      baseline,
      text: node.text,
      wordConfidence,
      speakerConfidence,
      alignmentConfidence,
      reviewScore: Math.round(
        (wordConfidence + speakerConfidence + alignmentConfidence) / 3
      ),
      reasons,
      provisional: false
    };
  });
};

function ReviewText({
  row,
  view,
  onSelect
}: {
  row: TranscriptRow;
  view: TranscriptView;
  onSelect: () => void;
}) {
  if (view === "baseline") return <>{row.baseline}</>;
  if (view === "redline") {
    return (
      <>
        {diffTranscript(row.baseline, row.text).map((token, index) => {
          if (token.kind === "added") return <ins key={index}>{token.value}</ins>;
          if (token.kind === "removed") return <del key={index}>{token.value}</del>;
          return <span key={index}>{token.value}</span>;
        })}
      </>
    );
  }

  if (!row.flaggedText || !row.text.includes(row.flaggedText)) return <>{row.text}</>;
  const [before, after] = row.text.split(row.flaggedText);
  return (
    <>
      {before}
      <button
        type="button"
        className={`confidence-word ${confidenceClass(row.reviewScore)}`}
        onClick={onSelect}
        aria-label={`${row.flaggedText}, review score ${row.reviewScore}`}
      >
        {row.flaggedText}
        {row.reviewScore < 85 && <sup aria-hidden="true">?</sup>}
      </button>
      {after}
    </>
  );
}

export function ReviewWorkspace({
  initialRows,
  matter,
  session,
  onRowsChange,
  onSessionChange,
  reviewerName,
  onOpenExport,
  onTitleChange,
  compositeMatterId,
  readOnly = false,
  onFinalise,
  onReloadComposite
}: ReviewWorkspaceProps) {
  const [rows, setRows] = useState<TranscriptRow[]>(
    initialRows ?? []
  );
  const [view, setView] = useState<TranscriptView>("clean");
  const [selectedId, setSelectedId] = useState(initialRows?.[0]?.id ?? "n-007");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editReason, setEditReason] = useState("Compared with source audio");
  const [edits, setEdits] = useState<EditRecord[]>([]);
  const [query, setQuery] = useState("");
  const [activeReason, setActiveReason] = useState<ReviewReason | "all">("all");
  const [isFrozen, setIsFrozen] = useState(
    session ? session.state === "frozen" : false
  );
  const [version, setVersion] = useState(session?.versionNumber ?? 1);
  const [operationState, setOperationState] = useState<
    "idle" | "loading" | "error"
  >(session ? "loading" : "idle");
  const [operationError, setOperationError] = useState("");
  const [audioPosition, setAudioPosition] = useState(session ? 0 : 26.68);
  const [playing, setPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(
    Math.max(45, ...rows.map((row) => row.endMs / 1000))
  );
  const [playbackRate, setPlaybackRate] = useState(1);
  const [comment, setComment] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [sessionFilter, setSessionFilter] = useState<number | "all">("all");
  const [reviewEvents, setReviewEvents] = useState<TranscriptReviewEvent[]>([]);
  const [materiallyEditedIds, setMateriallyEditedIds] = useState<string[]>([]);
  const [resolveAllConfirm, setResolveAllConfirm] = useState(false);
  const [resolveProgress, setResolveProgress] = useState("");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");
  const [copyFallback, setCopyFallback] = useState("");
  const batchEventIds = useRef(new Map<string, string>());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const minuteLines = useMemo(
    () => (FEATURE_FLAGS.minuteView ? minuteLineMap(rows) : new Map<string, number>()),
    [rows]
  );
  const displayLine = (row: TranscriptRow) => minuteLines.get(row.id) ?? row.line;

  useEffect(() => {
    if (!session || compositeMatterId) {
      setOperationState("idle");
      return;
    }
    setIsFrozen(session.state === "frozen");
    setVersion(session.versionNumber);
  }, [session]);

  useEffect(() => {
    if (!session) return;
    const controller = new AbortController();
    const loadReviewEvidence = async () => {
      setOperationState("loading");
      setOperationError("");
      try {
        const response = await fetch(
          apiUrl(`/api/transcripts/${encodeURIComponent(session.transcriptId)}`),
          { signal: controller.signal }
        );
        if (!response.ok) {
          throw await responseError(response, "Review evidence could not be loaded.");
        }
        const result = (await response.json()) as ReviewTranscriptPayload & {
          reviewEvents?: TranscriptReviewEvent[];
          version: {
            id: string;
            versionNumber: number;
            state: "draft" | "frozen";
            operations?: ReviewOperationSummary[];
          };
        };
        const loadedRows = rowsFromTranscript(result);
        setRows(loadedRows);
        onRowsChange(loadedRows);
        setSelectedId((current) =>
          loadedRows.some((row) => row.id === current)
            ? current
            : loadedRows[0]?.id ?? ""
        );
        setReviewEvents((current) =>
          mergedReviewEvents(current, result.reviewEvents ?? [])
        );
        setMateriallyEditedIds((current) => [
          ...new Set([
            ...current,
            ...materiallyEditedTranscriptNodes(result)
          ])
        ]);
        if (result.version.id !== session.versionId) {
          onSessionChange({
            ...session,
            versionId: result.version.id,
            versionNumber: result.version.versionNumber,
            state: result.version.state
          });
        }
        setOperationState("idle");
      } catch (cause) {
        if (controller.signal.aborted) return;
        setOperationState("error");
        setOperationError(
          cause instanceof Error ? cause.message : "Review evidence could not be loaded."
        );
      }
    };
    void loadReviewEvidence();
    return () => controller.abort();
  }, [compositeMatterId, session?.transcriptId, session?.versionId]);

  const selected = rows.find((row) => row.id === selectedId) ?? rows[0];
  const selectedRecordingId = selected?.sessionId ?? session?.recordingId;
  const sourceAudioApplies =
    !selected?.sessionId || selected.sessionId === session?.recordingId;
  const sourceAudio =
    selected?.sourceAudio ?? (sourceAudioApplies ? session?.sourceAudio : undefined);
  const sourceAudioDeleted = sourceAudio?.state === "deleted";
  const sourceAudioAvailable = Boolean(
    !sourceAudioDeleted && (selectedRecordingId || session?.audioUrl)
  );
  const sourceAudioLabel = (() => {
    if (sourceAudio?.state === "deleted") {
      return sourceAudio.deletedAt
        ? `Source audio deleted ${new Date(sourceAudio.deletedAt).toLocaleString()}`
        : "Source audio deleted";
    }
    if (sourceAudio?.state === "blocked-legal-hold") {
      return "Automatic deletion paused by legal hold";
    }
    if (sourceAudio?.state === "delayed-processing") {
      return "Automatic deletion waiting for successful transcription";
    }
    if (sourceAudio?.state === "scheduled" && sourceAudio.deleteBy) {
      return `Source audio scheduled for deletion by ${new Date(
        sourceAudio.deleteBy
      ).toLocaleString()}`;
    }
    return sourceAudioAvailable
      ? "Source audio · click text to seek"
      : "Source player available after live capture";
  })();
  const activeVersionId = session?.versionId ?? DEMO_VERSION_ID;
  const currentReviewEvents = useMemo(
    () =>
      compositeMatterId
        ? reviewEvents
        : reviewEvents.filter((event) => event.versionId === activeVersionId),
    [activeVersionId, compositeMatterId, reviewEvents]
  );
  const checkedIds = useMemo(
    () =>
      new Set(
        currentReviewEvents.flatMap((event) =>
          event.kind === "checked" && event.nodeId ? [event.nodeId] : []
        )
      ),
    [currentReviewEvents]
  );
  const bookmarkedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const event of currentReviewEvents) {
      if (!event.nodeId) continue;
      if (event.kind === "bookmark-added") ids.add(event.nodeId);
      if (event.kind === "bookmark-removed") ids.delete(event.nodeId);
    }
    return [...ids];
  }, [currentReviewEvents]);
  const comments = useMemo(
    () => currentReviewEvents.filter((event) => event.kind === "comment"),
    [currentReviewEvents]
  );
  const resolvedIds = useMemo(
    () =>
      new Set([
        ...checkedIds,
        ...materiallyEditedIds,
        ...rows.filter((row) => row.reviewResolved).map((row) => row.id)
      ]),
    [checkedIds, materiallyEditedIds, rows]
  );
  const reviewQueue = useMemo(
    () =>
      rows
        .filter((row) => row.reasons.length > 0)
        .filter((row) => !resolvedIds.has(row.id))
        .filter(
          (row) => sessionFilter === "all" || row.sessionOrdinal === sessionFilter
        )
        .filter((row) => activeReason === "all" || row.reasons.includes(activeReason))
        .filter((row) => `${row.speaker} ${row.text}`.toLowerCase().includes(query.toLowerCase()))
        .sort((a, b) => a.reviewScore - b.reviewScore),
    [activeReason, query, resolvedIds, rows, sessionFilter]
  );

  const updateRows = (next: TranscriptRow[]) => {
    setRows(next);
    onRowsChange(next);
  };

  const applyRemoteOperations = async (operations: ReviewOperationSummary[]) => {
    if (!session) return null;
    setOperationState("loading");
    setOperationError("");
    const groups = new Map<
      string,
      { transcriptId: string; versionId: string; operations: ReviewOperationSummary[] }
    >();
    for (const operation of operations) {
      const targetNodeId = operation.targetNodeId ?? operation.afterNodeId;
      const targetRow = rows.find((row) => row.id === targetNodeId) ?? selected;
      const transcriptId = targetRow?.sourceTranscriptId ?? session.transcriptId;
      const versionId = targetRow?.sourceVersionId ?? session.versionId;
      const key = `${transcriptId}:${versionId}`;
      const group = groups.get(key) ?? { transcriptId, versionId, operations: [] };
      group.operations.push(operation);
      groups.set(key, group);
    }
    type OperationResult = ReviewTranscriptPayload & {
      version: {
        id: string;
        versionNumber: number;
        state: "draft" | "frozen";
        operations?: ReviewOperationSummary[];
      };
    };
    let result: OperationResult | null = null;
    for (const group of groups.values()) {
      const response = await fetch(
        apiUrl(`/api/transcripts/${encodeURIComponent(group.transcriptId)}/operations`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            expectedVersionId: group.versionId,
            operations: group.operations
          })
        }
      );
      if (!response.ok) {
        const failure = await responseError(response, "The transcript change was not recorded.");
        setOperationState("error");
        setOperationError(failure.message);
        throw failure;
      }
      result = (await response.json()) as OperationResult;
    }
    if (!result) return null;
    const nextSession: TranscriptSession = {
      ...session,
      versionId: result.version.id,
      versionNumber: result.version.versionNumber,
      state: result.version.state
    };
    if (!compositeMatterId) onSessionChange(nextSession);
    if (compositeMatterId && onReloadComposite) {
      updateRows(await onReloadComposite());
    } else {
      updateRows(rowsFromTranscript(result));
    }
    setVersion(result.version.versionNumber);
    setIsFrozen(result.version.state === "frozen");
    setMateriallyEditedIds(materiallyEditedTranscriptNodes(result));
    setOperationState("idle");
    return nextSession;
  };

  const appendReviewEvent = async (
    kind: TranscriptReviewEventKind,
    nodeId: string,
    text?: string
  ) => {
    const id = crypto.randomUUID();
    if (!session) {
      setReviewEvents((current) => [
        ...current,
        {
          id,
          transcriptId: "demo-transcript",
          versionId: activeVersionId,
          nodeId,
          kind,
          ...(text === undefined ? {} : { text }),
          actorId: reviewerName,
          createdAt: new Date().toISOString()
        }
      ]);
      return true;
    }

    setOperationState("loading");
    setOperationError("");
    try {
      const targetRow = rows.find((row) => row.id === nodeId);
      const targetTranscriptId = targetRow?.sourceTranscriptId ?? session.transcriptId;
      const targetVersionId = targetRow?.sourceVersionId ?? session.versionId;
      const response = await fetch(
        apiUrl(`/api/transcripts/${encodeURIComponent(targetTranscriptId)}/review-events`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            expectedVersionId: targetVersionId,
            event: {
              id,
              kind,
              nodeId,
              ...(text === undefined ? {} : { text })
            }
          })
        }
      );
      if (!response.ok) {
        throw await responseError(response, "Review evidence could not be recorded.");
      }
      const result = (await response.json()) as {
        reviewEvent: TranscriptReviewEvent;
        reviewEvents?: TranscriptReviewEvent[];
      };
      setReviewEvents((current) =>
        mergedReviewEvents(
          current,
          result.reviewEvents ?? [result.reviewEvent]
        )
      );
      setOperationState("idle");
      return true;
    } catch (cause) {
      setOperationState("error");
      setOperationError(
        cause instanceof Error ? cause.message : "Review evidence could not be recorded."
      );
      return false;
    }
  };

  const startEdit = (row: TranscriptRow) => {
    if (readOnly || operationState === "loading") return;
    setSelectedId(row.id);
    setEditingId(row.id);
    setEditValue(row.text);
    setEditReason("Compared with source audio");
  };

  const saveEdit = async (event: FormEvent) => {
    event.preventDefault();
    const row = rows.find((candidate) => candidate.id === editingId);
    const after = editValue.trim();
    if (!row || !after || after === row.text) {
      setEditingId(null);
      return;
    }
    try {
      await applyRemoteOperations([
        {
          kind: "replace-text",
          targetNodeId: row.id,
          beforeText: row.text,
          afterText: after,
          reason: editReason,
          audioRange: { startMs: row.startMs, endMs: row.endMs }
        }
      ]);
    } catch {
      return;
    }
    const next = rows.map((candidate) =>
      candidate.id === row.id
        ? {
            ...candidate,
            text: after,
            reviewScore: 100,
            reasons: candidate.reasons.filter(
              (reason) => reason !== "low-confidence" && reason !== "provider-disagreement"
            ),
            flaggedText: undefined
          }
        : candidate
    );
    const operation: EditRecord = {
      id: crypto.randomUUID(),
      rowId: row.id,
      actor: reviewerName,
      at: new Date().toISOString(),
      before: row.text,
      after,
      reason: editReason,
      audioStartMs: row.startMs,
      audioEndMs: row.endMs
    };
    setEdits((current) => [...current, operation]);
    if (!session && isFrozen) {
      setVersion((current) => current + 1);
      setIsFrozen(false);
    }
    updateRows(next);
    setEditingId(null);
    setView("redline");
  };

  const markReviewed = async (row: TranscriptRow) => {
    await appendReviewEvent("checked", row.id);
  };

  const markAllReviewed = async () => {
    const unresolved = reviewQueue.filter((row) => !checkedIds.has(row.id));
    if (unresolved.length === 0) {
      setResolveAllConfirm(false);
      return;
    }
    if (!session) {
      const createdAt = new Date().toISOString();
      setReviewEvents((current) => [
        ...current,
        ...unresolved.map((row) => ({
          id: crypto.randomUUID(),
          transcriptId: "demo-transcript",
          versionId: activeVersionId,
          nodeId: row.id,
          kind: "checked" as const,
          actorId: reviewerName,
          createdAt
        }))
      ]);
      setResolveAllConfirm(false);
      return;
    }

    setOperationState("loading");
    setOperationError("");
    setResolveProgress(`Resolving 0 of ${unresolved.length}`);
    const groups = new Map<string, { transcriptId: string; versionId: string; rows: TranscriptRow[] }>();
    for (const row of unresolved) {
      const transcriptId = row.sourceTranscriptId ?? session.transcriptId;
      const versionId = row.sourceVersionId ?? session.versionId;
      const key = `${transcriptId}:${versionId}`;
      const group = groups.get(key) ?? { transcriptId, versionId, rows: [] };
      group.rows.push(row);
      groups.set(key, group);
    }
    let completed = 0;
    try {
      for (const group of groups.values()) {
        for (let offset = 0; offset < group.rows.length; offset += 100) {
          const chunk = group.rows.slice(offset, offset + 100);
          const events = chunk.map((row) => {
            const eventKey = `${group.transcriptId}:${group.versionId}:${row.id}`;
            const id = batchEventIds.current.get(eventKey) ?? crypto.randomUUID();
            batchEventIds.current.set(eventKey, id);
            return { id, kind: "checked", nodeId: row.id };
          });
          const response = await fetch(
            apiUrl(`/api/transcripts/${encodeURIComponent(group.transcriptId)}/review-events/batch`),
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ expectedVersionId: group.versionId, events })
            }
          );
          if (!response.ok) {
            throw await responseError(response, "Review evidence could not be recorded.");
          }
          const result = (await response.json()) as {
            reviewEvents?: TranscriptReviewEvent[];
            recordedEvents?: TranscriptReviewEvent[];
          };
          setReviewEvents((current) =>
            mergedReviewEvents(current, result.reviewEvents ?? result.recordedEvents ?? [])
          );
          for (const row of chunk) {
            batchEventIds.current.delete(`${group.transcriptId}:${group.versionId}:${row.id}`);
          }
          completed += chunk.length;
          setResolveProgress(`Resolving ${completed} of ${unresolved.length}`);
        }
      }
      setOperationState("idle");
      setResolveAllConfirm(false);
      setResolveProgress("");
    } catch (cause) {
      setOperationState("error");
      setOperationError(
        cause instanceof Error ? cause.message : "Review evidence could not be recorded."
      );
      setResolveProgress("");
    }
  };

  /**
   * Speaker attribution for the transcript.
   *
   * `scope: "line"` reassigns only the selected line — the correction that
   * matters when recognition merged two people into one identity, since the
   * new speaker identity is what regroups the projection. `scope: "speaker"`
   * relabels every line sharing the current identity, which is the right
   * move when the model got the grouping right but the name wrong.
   */
  const visibleRows = useMemo(
    () =>
      sessionFilter === "all"
        ? rows
        : rows.filter((row) => row.sessionOrdinal === sessionFilter),
    [rows, sessionFilter]
  );
  const sessionOrdinals = useMemo(
    () => [...new Set(rows.flatMap((row) => (row.sessionOrdinal ? [row.sessionOrdinal] : [])))],
    [rows]
  );
  const pageGroups = useMemo(
    () => previewPageGroups(visibleRows, matter.jurisdictionPack),
    [visibleRows, matter.jurisdictionPack]
  );

  const copyText = async (text: string) => {
    setCopyFallback("");
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(text);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1800);
    } catch {
      setCopyState("error");
      setCopyFallback(text);
    }
  };

  const copySelectedPage = () => {
    const group = pageGroups.find((candidate) =>
      candidate.rows.some((row) => row.id === selected?.id)
    ) ?? pageGroups[0];
    if (!group) return;
    void copyText(cleanTranscriptClipboardPage(group.page, group.rows));
  };

  const sameSpeakerCount = selected
    ? rows.filter((row) => row.speakerId === selected.speakerId).length
    : 0;

  const assignSpeaker = async (
    speakerName: string,
    scope: "line" | "speaker"
  ) => {
    if (!selected) return;
    const before = selected.speaker;
    // Participant names are the stable identity the projection groups on;
    // "unknown" is the sentinel for an unattributed node, which the domain
    // represents as a null speakerId.
    const nextSpeakerId = speakerName;
    const affected =
      scope === "speaker"
        ? rows.filter((row) => row.speakerId === selected.speakerId)
        : [selected];
    if (
      affected.length === 0 ||
      (scope === "speaker" && speakerName === selected.speaker)
    ) {
      return;
    }
    try {
      await applyRemoteOperations(
        affected.map((row) => ({
          kind: "set-speaker",
          targetNodeId: row.id,
          beforeSpeakerId: row.speakerId === "unknown" ? null : row.speakerId,
          afterSpeaker: {
            speakerId: nextSpeakerId,
            label: speakerName
          },
          reason:
            scope === "line"
              ? "Speaker reassigned for this line"
              : "Speaker attribution corrected",
          audioRange: { startMs: row.startMs, endMs: row.endMs }
        }))
      );
    } catch {
      return;
    }
    const affectedIds = new Set(affected.map((row) => row.id));
    updateRows(
      rows.map((row) =>
        affectedIds.has(row.id)
          ? {
              ...row,
              speaker: speakerName,
              speakerId: nextSpeakerId,
              role: roleMarker(matter.participants, speakerName)
            }
          : row
      )
    );
    setEdits((current) => [
      ...current,
      {
        id: crypto.randomUUID(),
        rowId: selected.id,
        actor: reviewerName,
        at: new Date().toISOString(),
        before,
        after: speakerName,
        reason:
          scope === "line"
            ? "Speaker reassigned for this line"
            : "Speaker attribution corrected",
        audioStartMs: selected.startMs,
        audioEndMs: selected.endMs
      }
    ]);
    if (!session && isFrozen) {
      setVersion((current) => current + 1);
      setIsFrozen(false);
    }
  };

  const addComment = async (event: FormEvent) => {
    event.preventDefault();
    if (!comment.trim() || !selected) return;
    const recorded = await appendReviewEvent(
      "comment",
      selected.id,
      comment.trim()
    );
    if (recorded) setComment("");
  };

  const toggleBookmark = async () => {
    if (!selected) return;
    await appendReviewEvent(
      bookmarkedIds.includes(selected.id) ? "bookmark-removed" : "bookmark-added",
      selected.id
    );
  };

  const freezeVersion = async () => {
    if (!session) {
      setIsFrozen(true);
      onOpenExport();
      return;
    }
    setOperationState("loading");
    setOperationError("");
    try {
      const response = await fetch(
        apiUrl(`/api/transcripts/${encodeURIComponent(session.transcriptId)}/freeze`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ expectedVersionId: session.versionId })
        }
      );
      if (!response.ok) {
        throw await responseError(response, "The transcript version could not be frozen.");
      }
      const result = (await response.json()) as {
        version: { id: string; versionNumber: number; state: "frozen" };
      };
      const nextSession: TranscriptSession = {
        ...session,
        versionId: result.version.id,
        versionNumber: result.version.versionNumber,
        state: result.version.state
      };
      onSessionChange(nextSession);
      setVersion(result.version.versionNumber);
      setIsFrozen(true);
      setOperationState("idle");
      onOpenExport();
    } catch (cause) {
      setOperationState("error");
      setOperationError(
        cause instanceof Error ? cause.message : "The transcript version could not be frozen."
      );
    }
  };

  const seekAudio = (seconds: number) => {
    const position = Math.max(0, Math.min(audioDuration, seconds));
    setAudioPosition(position);
    if (audioRef.current) audioRef.current.currentTime = position;
  };

  const toggleAudio = async () => {
    const audio = audioRef.current;
    if (!audio || !sourceAudioAvailable) return;
    if (audio.paused) {
      try {
        await audio.play();
        setPlaying(true);
      } catch {
        setOperationError("Source audio could not start. Use the source download from Live capture.");
      }
    } else {
      audio.pause();
      setPlaying(false);
    }
  };

  const refreshSourceAudio = async () => {
    if (!selectedRecordingId) return;
    const response = await fetch(
      apiUrl(`/api/recordings/${encodeURIComponent(selectedRecordingId)}`)
    );
    if (!response.ok) return;
    const result = (await response.json()) as {
      sourceAudio?: TranscriptSession["sourceAudio"];
    };
    if (!result.sourceAudio) return;
    if (session?.recordingId === selectedRecordingId) {
      onSessionChange({ ...session, sourceAudio: result.sourceAudio });
    }
    updateRows(
      rows.map((row) =>
        row.sessionId === selectedRecordingId
          ? { ...row, sourceAudio: result.sourceAudio }
          : row
      )
    );
  };

  const cyclePlaybackRate = () => {
    const rates = [1, 1.25, 1.5, 0.75];
    const next = rates[(rates.indexOf(playbackRate) + 1) % rates.length] ?? 1;
    setPlaybackRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const reasonFilters: Array<ReviewReason | "all"> = [
    "all",
    "low-confidence",
    "uncertain-speaker",
    "provider-disagreement",
    "overlap",
    "name",
    "number",
    "date"
  ];

  return (
    <div className="review-workbench">
      <header className="review-header">
        <input
          className="recorder-title recorder-title--review"
          value={matter.title}
          onChange={(event) => onTitleChange?.(event.target.value)}
          disabled={readOnly}
          aria-label="Transcription title"
          placeholder={displayTranscriptTitle("", matter.date)}
        />
        <div className="review-header__actions">
          {readOnly || isFrozen ? (
            <button className="button button--primary" type="button" onClick={onOpenExport}>
              Export
            </button>
          ) : compositeMatterId && onFinalise ? (
            <button
              className="button button--primary"
              type="button"
              onClick={() => {
                if (reviewQueue.length) {
                  setResolveAllConfirm(true);
                  return;
                }
                void onFinalise();
              }}
              disabled={operationState === "loading"}
            >
              {reviewQueue.length ? `Resolve all (${reviewQueue.length})` : "Finalise transcript"}
            </button>
          ) : !isFrozen ? (
            <button
              className="button button--primary"
              type="button"
              onClick={() => {
                if (reviewQueue.length) {
                  setResolveAllConfirm(true);
                  return;
                }
                void freezeVersion();
              }}
              disabled={operationState === "loading"}
              aria-busy={operationState === "loading"}
              title={
                reviewQueue.length
                  ? "Mark every queued item checked or correct it before freezing."
                  : undefined
              }
            >
              {operationState === "loading"
                ? "Preparing…"
                : reviewQueue.length
                  ? `Resolve all (${reviewQueue.length})`
                  : "Prepare export"}
            </button>
          ) : null}
        </div>
      </header>

      {operationError && (
        <div className="notice notice--warning" role="alert">
          <AlertTriangle size={17} aria-hidden="true" />
          <span>{operationError}</span>
        </div>
      )}

      <section className="review-guidance" aria-label="Review guidance">
        <p>Please review the transcript before finalising it.</p>
        <ul className="review-confidence-legend" aria-label="Confidence legend">
          <li><i className="is-high" aria-hidden="true" /> High · clear</li>
          <li><i className="is-medium" aria-hidden="true" /> Medium · check</li>
          <li><i className="is-low" aria-hidden="true" /> Low · check</li>
        </ul>
      </section>

      {resolveAllConfirm && (
        <section className="resolve-all-confirm" aria-labelledby="resolve-all-title">
          <div>
            <strong id="resolve-all-title">Acknowledge {reviewQueue.length} review items?</strong>
            <p>This records each item as checked. Transcript wording will not change.</p>
            {resolveProgress && <small aria-live="polite">{resolveProgress}</small>}
          </div>
          <button
            className="button button--text"
            type="button"
            onClick={() => setResolveAllConfirm(false)}
            disabled={operationState === "loading"}
          >
            Cancel
          </button>
          <button
            className="button button--primary"
            type="button"
            onClick={() => void markAllReviewed()}
            disabled={operationState === "loading"}
            aria-busy={operationState === "loading"}
          >
            Confirm Resolve all
          </button>
        </section>
      )}

      <div className="review-toolbar" aria-label="Transcript tools">
        <div className="segmented" role="group" aria-label="Transcript view">
          {(["clean", "redline", "baseline"] as TranscriptView[]).map((mode) => (
            <button
              type="button"
              key={mode}
              className={view === mode ? "is-active" : ""}
              aria-pressed={view === mode}
              onClick={() => setView(mode)}
            >
              {mode === "baseline" ? "Machine baseline" : `${mode[0].toUpperCase()}${mode.slice(1)}`}
            </button>
          ))}
        </div>
        {sessionOrdinals.length > 1 && (
          <div className="session-filter" role="group" aria-label="Filter recording sessions">
            <button
              type="button"
              className={sessionFilter === "all" ? "is-active" : ""}
              onClick={() => setSessionFilter("all")}
            >
              All sessions
            </button>
            {sessionOrdinals.map((ordinal) => (
              <button
                type="button"
                key={ordinal}
                className={sessionFilter === ordinal ? "is-active" : ""}
                onClick={() => setSessionFilter(ordinal)}
              >
                Session {ordinal}
              </button>
            ))}
          </div>
        )}
        <div className="review-tools">
          <button
            className="button button--text"
            type="button"
            onClick={() => setDetailsOpen((current) => !current)}
            aria-expanded={detailsOpen}
          >
            <Filter size={15} aria-hidden="true" />
            Queue
          </button>
          <button
            className="button button--text"
            type="button"
            onClick={() => selected && void copyText(cleanTranscriptClipboardRow(selected))}
            disabled={!selected}
          >
            <Copy size={15} aria-hidden="true" /> Copy paragraph
          </button>
          <button
            className="button button--text"
            type="button"
            onClick={copySelectedPage}
            disabled={pageGroups.length === 0}
          >
            <Copy size={15} aria-hidden="true" /> Copy page
          </button>
          <button
            className="button button--text"
            type="button"
            onClick={() => void copyText(cleanTranscriptClipboard(visibleRows))}
            disabled={visibleRows.length === 0}
          >
            <Copy size={15} aria-hidden="true" /> Copy transcript
          </button>
          <button
            className="button button--text"
            type="button"
            onClick={() => {
              searchRef.current?.focus();
              searchRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
            }}
          >
            <Search size={15} aria-hidden="true" /> Find
          </button>
          <button
            className="button button--text"
            type="button"
            onClick={() => setView("redline")}
            title="Show transcript changes"
          >
            <History size={15} aria-hidden="true" /> {edits.length} edits
          </button>
          <button
            className="button button--text"
            type="button"
            onClick={toggleBookmark}
            disabled={!selected || operationState === "loading"}
            aria-pressed={selected ? bookmarkedIds.includes(selected.id) : false}
          >
            <Bookmark size={15} aria-hidden="true" />
            {selected && bookmarkedIds.includes(selected.id)
              ? "Remove bookmark"
              : `Bookmark${bookmarkedIds.length ? ` (${bookmarkedIds.length})` : ""}`}
          </button>
        </div>
      </div>

      <p className="copy-status" role="status" aria-live="polite">
        {copyState === "copied"
          ? "Copied"
          : copyState === "error"
            ? "Clipboard blocked. Select the text below and copy it."
            : ""}
      </p>
      {copyFallback && (
        <textarea
          className="copy-fallback"
          aria-label="Transcript text to copy"
          value={copyFallback}
          readOnly
          onFocus={(event) => event.currentTarget.select()}
        />
      )}

      <div className={`review-layout ${detailsOpen ? "has-sidebar" : ""}`}>
        <main
          className={`transcript-sheet ${
            rows.length < 8 ? "transcript-sheet--short" : ""
          }`}
          aria-label={`${view} transcript`}
        >
          <header className="transcript-sheet__head">
            <span>
              {pageGroups.length > 1
                ? `${pageGroups.length} pages`
                : "Page 1"}
            </span>
            <span>{matter.date}</span>
          </header>
          <div className="review-lines">
            {rows.length === 0 && (
              <div className="transcript-empty">
                <strong>No speech was detected.</strong>
                <span>Source recording is preserved. Add a gap or inaudible note before freezing if needed.</span>
              </div>
            )}
            {pageGroups.map((group) => (
              <section className="sheet-page" key={group.page}>
                {group.rows.map((row) => {
                  const rowIndex = rows.findIndex((candidate) => candidate.id === row.id);
                  const previous = rows[rowIndex - 1];
                  const blockLine = displayLine(row);
                  const startsMinute = !previous || displayLine(previous) !== blockLine;
                  const startsSession =
                    row.sessionOrdinal !== undefined &&
                    previous?.sessionOrdinal !== row.sessionOrdinal;
                  return (
              <Fragment key={row.id}>
                {startsSession && (
                  <div className="session-divider" role="separator">
                    <strong>Session {row.sessionOrdinal}</strong>
                    <span>
                      {row.sessionStartedAt
                        ? new Intl.DateTimeFormat(undefined, {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit"
                          }).format(new Date(row.sessionStartedAt))
                        : "Captured session"}
                      {row.sessionLabel ? ` · ${row.sessionLabel}` : ""}
                    </span>
                  </div>
                )}
              <article
                className={[
                  "review-line",
                  row.kind === "exhibit" || row.kind === "adjournment"
                    ? "review-line--action"
                    : "",
                  row.reasons.length > 0
                    ? `review-line--confidence-${confidenceBand(row.reviewScore)}`
                    : "",
                  startsMinute ? "review-line--minute-start" : "review-line--minute-segment",
                  selectedId === row.id ? "review-line--selected" : "",
                  bookmarkedIds.includes(row.id) ? "review-line--bookmarked" : ""
                ]
                  .filter(Boolean)
                  .join(" ")}
                onDoubleClick={() => startEdit(row)}
              >
                <button
                  className="line-number"
                  type="button"
                  aria-label={`Select line ${row.line}`}
                  title={`Minute line ${blockLine}, exact segment ${timecode(row.startMs)}`}
                  onClick={() => {
                    setSelectedId(row.id);
                    seekAudio(row.startMs / 1000);
                  }}
                >
                  {startsMinute ? blockLine : "↳"}
                </button>
                <button
                  className="timecode"
                  type="button"
                  onClick={() => seekAudio(row.startMs / 1000)}
                >
                  {transcriptTime(row, session?.recordedAtEpochMs)}
                </button>
                {row.kind === "exhibit" || row.kind === "adjournment" ? (
                  <span className="speaker-label" aria-hidden="true" />
                ) : (
                  <button
                    className="speaker-label"
                    type="button"
                    onClick={() => setSelectedId(row.id)}
                    title={row.speaker}
                  >
                    {row.role}
                  </button>
                )}
                <div className="review-copy">
                  {editingId === row.id ? (
                    <form className="line-editor" onSubmit={saveEdit}>
                      <label>
                        <span>Edit minute line {blockLine} segment</span>
                        <textarea
                          value={editValue}
                          onChange={(event) => setEditValue(event.target.value)}
                          autoFocus
                        />
                      </label>
                      <label>
                        <span>Reason</span>
                        <select
                          value={editReason}
                          onChange={(event) => setEditReason(event.target.value)}
                        >
                          <option>Compared with source audio</option>
                          <option>Corrected proper noun</option>
                          <option>Corrected speaker attribution</option>
                          <option>Applied agreed style</option>
                          <option>Other — noted in comment</option>
                        </select>
                      </label>
                      <span className="line-editor__actions">
                        <button
                          type="button"
                          className="button button--text"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="button button--primary"
                          disabled={operationState === "loading"}
                        >
                          Record change
                        </button>
                      </span>
                    </form>
                  ) : (
                    <p
                      onClick={() => {
                        setSelectedId(row.id);
                        seekAudio(row.startMs / 1000);
                      }}
                    >
                      <ReviewText row={row} view={view} onSelect={() => setSelectedId(row.id)} />
                    </p>
                  )}
                  {editingId !== row.id && row.reasons.length > 0 && (
                    <small className="review-reason">
                      {row.reasons.map((reason) => REASON_LABELS[reason]).join(" · ")}
                      {" · compare with audio"}
                    </small>
                  )}
                </div>
                <span className="row-actions">
                  {bookmarkedIds.includes(row.id) && (
                    <Bookmark size={14} aria-label="Bookmarked line" />
                  )}
                  {row.reasons.length > 0 && row.reviewScore < 85 && (
                    <span className="issue-marker">
                      <AlertTriangle size={15} aria-hidden="true" />
                      <span className="sr-only">
                        {confidenceBand(row.reviewScore) === "low" ? "Low" : "Medium"} confidence;
                        check against audio. Review score {row.reviewScore}.
                      </span>
                    </span>
                  )}
                  {row.kind !== "exhibit" && row.kind !== "adjournment" && (
                    <button type="button" onClick={() => startEdit(row)}>
                      Edit
                    </button>
                  )}
                </span>
              </article>
              </Fragment>
                  );
                })}
                {pageGroups.length > 1 && (
                  <footer
                    className="sheet-page__break"
                    aria-label={`End of page ${group.page}`}
                  >
                    <span>
                      Page {group.page} of {pageGroups.length}
                    </span>
                  </footer>
                )}
              </section>
            ))}
          </div>
          <footer className="transcript-page-footer">
            <span>
              {pageGroups.length === 1
                ? "Page 1 of 1"
                : `${pageGroups.length} pages`}
              {!isFrozen && rows.length > 0 && (
                <small> · numbering fixed at freeze</small>
              )}
            </span>
            <span>Version {version}</span>
          </footer>
        </main>

        {detailsOpen && <aside className="review-sidebar">
          <section className="review-queue" aria-labelledby="review-queue-title">
            <header>
              <div>
                <p className="overline">Review queue</p>
                <h2 id="review-queue-title">{reviewQueue.length} items</h2>
              </div>
              <Filter size={17} aria-hidden="true" />
            </header>
            <label className="search-field">
              <Search size={16} aria-hidden="true" />
              <span className="sr-only">Search review queue</span>
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search transcript"
              />
            </label>
            <div className="filter-row">
              <select
                aria-label="Review reason"
                value={activeReason}
                onChange={(event) =>
                  setActiveReason(event.target.value as ReviewReason | "all")
                }
              >
                {reasonFilters.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason === "all" ? "All review reasons" : REASON_LABELS[reason]}
                  </option>
                ))}
              </select>
              <ChevronDown size={15} aria-hidden="true" />
            </div>
            {reviewQueue.length > 0 && !isFrozen && (
              <button
                className="button button--quiet queue-bulk-resolve"
                type="button"
                onClick={() => setResolveAllConfirm(true)}
                disabled={operationState === "loading"}
              >
                <Check size={15} aria-hidden="true" />
                Resolve all {reviewQueue.length}
              </button>
            )}
            <ol>
              {reviewQueue.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    className={row.id === selectedId ? "is-active" : ""}
                    onClick={() => {
                      setSelectedId(row.id);
                      seekAudio(row.startMs / 1000);
                    }}
                  >
                    <span className={`review-score review-score--${confidenceBand(row.reviewScore)}`}>
                      {row.reviewScore}
                    </span>
                    <span>
                      <strong>Line {displayLine(row)} · {row.speaker}</strong>
                      <small>{row.reasons.map((reason) => REASON_LABELS[reason]).join(" · ")}</small>
                    </span>
                    <Clock3 size={14} aria-hidden="true" />
                  </button>
                </li>
              ))}
              {!reviewQueue.length && (
                <li className="queue-complete">
                  <Check size={18} aria-hidden="true" />
                  Review queue clear.
                </li>
              )}
            </ol>
          </section>

          {selected && selected.kind !== "exhibit" && selected.kind !== "adjournment" && (
            <section className="confidence-inspector" aria-labelledby="confidence-title">
              <header>
                <div>
                  <p className="overline">Evidence inspector</p>
                  <h2 id="confidence-title">Line {displayLine(selected)}</h2>
                </div>
                <Gauge size={17} aria-hidden="true" />
              </header>
              <dl>
                <div>
                  <dt>Word confidence</dt>
                  <dd>{selected.wordConfidence}</dd>
                </div>
                <div>
                  <dt>Speaker confidence</dt>
                  <dd>{selected.speakerConfidence}</dd>
                </div>
                <div>
                  <dt>Alignment confidence</dt>
                  <dd>{selected.alignmentConfidence}</dd>
                </div>
                <div className="score-total">
                  <dt>Review score</dt>
                  <dd>{selected.reviewScore}</dd>
                </div>
              </dl>
              <p className="inspector-note">
                Review score combines model evidence and disagreement. It is not a probability of
                legal accuracy.
              </p>
              {selected.reasons.includes("provider-disagreement") && (
                <p className="inspector-note">
                  Check against audio means transcription passes disagreed. It does not mean one
                  named provider is necessarily right or wrong.
                </p>
              )}
              <div className="field speaker-assign">
                <span className="field-label">
                  <UserRoundCheck size={15} aria-hidden="true" /> Speaker
                </span>
                <select
                  aria-label="Speaker for this line"
                  value={selected.speaker}
                  onChange={(event) => assignSpeaker(event.target.value, "line")}
                  disabled={operationState === "loading"}
                >
                  {!matter.participants.some(
                    (participant) => participant.name === selected.speaker
                  ) && <option>{selected.speaker}</option>}
                  {matter.participants.map((participant) => (
                    <option key={participant.name}>{participant.name}</option>
                  ))}
                </select>
                <small>
                  Changes this line only — use it when two people were merged
                  into one speaker.
                </small>
                {sameSpeakerCount > 1 && (
                  <button
                    className="button button--text"
                    type="button"
                    disabled={operationState === "loading"}
                    onClick={() => assignSpeaker(selected.speaker, "speaker")}
                  >
                    Apply to all {sameSpeakerCount} lines by this speaker
                  </button>
                )}
              </div>
              <div className="inspector-actions">
                <button className="button button--quiet" type="button" onClick={() => startEdit(selected)}>
                  Edit line
                </button>
                <button
                  className="button button--primary"
                  type="button"
                  onClick={() => markReviewed(selected)}
                  disabled={operationState === "loading" || checkedIds.has(selected.id)}
                >
                  <Check size={15} aria-hidden="true" />{" "}
                  {checkedIds.has(selected.id) ? "Checked" : "Mark checked"}
                </button>
              </div>
            </section>
          )}

          <section className="comments-panel">
            <header>
              <div>
                <p className="overline">Counsel notes</p>
                <h2>{comments.length} comments</h2>
              </div>
              <MessageSquare size={17} aria-hidden="true" />
            </header>
            <form onSubmit={addComment}>
              <label className="field">
                <span>Comment on line {selected?.line ?? "—"}</span>
                <textarea
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  placeholder="Note for reviewer"
                />
              </label>
              <button
                className="button button--quiet"
                type="submit"
                disabled={operationState === "loading" || !selected}
              >
                Add comment
              </button>
            </form>
            {comments.length > 0 && (
              <ol className="comment-list" aria-label="Counsel comments">
                {comments.map((item) => {
                  const line = rows.find((row) => row.id === item.nodeId)?.line;
                  return (
                    <li key={item.id}>
                      <strong>{line ? `Line ${line}` : "Transcript"}</strong>
                      <span>{item.text}</span>
                      <small>{item.actorId}</small>
                      <time dateTime={item.createdAt}>
                        {new Date(item.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </time>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        </aside>}
      </div>

      <aside className="audio-dock" aria-label="Synced source audio">
        {sourceAudioAvailable && (
          <audio
            ref={audioRef}
            src={
              selected?.sessionId
                ? apiUrl(`/api/recordings/${encodeURIComponent(selected.sessionId)}/audio`)
                : session?.audioUrl ??
                  (session?.recordingId
                    ? apiUrl(
                        `/api/recordings/${encodeURIComponent(session.recordingId)}/audio`
                      )
                    : undefined)
            }
            preload="metadata"
            onLoadedMetadata={(event) => {
              const duration = event.currentTarget.duration;
              if (Number.isFinite(duration)) setAudioDuration(duration);
            }}
            onTimeUpdate={(event) => setAudioPosition(event.currentTarget.currentTime)}
            onEnded={() => setPlaying(false)}
            onError={() => void refreshSourceAudio()}
          />
        )}
        <button
          type="button"
          className="audio-play"
          aria-label={playing ? "Pause source audio" : "Play source audio"}
          onClick={toggleAudio}
          disabled={!sourceAudioAvailable}
        >
          {playing ? <Pause size={17} aria-hidden="true" /> : <Play size={17} aria-hidden="true" />}
        </button>
        <span className="audio-time mono">{timecode(audioPosition * 1000)}</span>
        <label className="audio-range">
          <span className="sr-only">Source audio position</span>
          <input
            type="range"
            min={0}
            max={audioDuration}
            step={0.01}
            value={audioPosition}
            onChange={(event) => seekAudio(Number(event.target.value))}
            disabled={!sourceAudioAvailable}
          />
        </label>
        <span className="audio-time mono">{timecode(audioDuration * 1000)}</span>
        <button
          className="button button--text"
          type="button"
          onClick={cyclePlaybackRate}
          disabled={!sourceAudioAvailable}
        >
          {playbackRate}×
        </button>
        <span className="audio-source">
          <Volume2 size={15} aria-hidden="true" />
          {sourceAudioLabel}
        </span>
      </aside>
    </div>
  );
}
